import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { buildCustomerMap } from "@/lib/customerNames";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Receipt, Mail, Printer, Download, XCircle, PlusCircle, ExternalLink, AlertCircle, CheckCircle2,
  Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";

const STATUS = {
  draft: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-50 text-red-700",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function ProformaInvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState([]);
  const [csos, setCsos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createCsoId, setCreateCsoId] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [lineEditIdx, setLineEditIdx] = useState(null);
  const [lineEditForm, setLineEditForm] = useState({ qty: "", price: "", discount_pct: "", gst_pct: "", delivery_date: "" });

  const [lineDeleteIdx, setLineDeleteIdx] = useState(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPayload, setEmailPayload] = useState({ to_email: "", cc_email: "", bcc_email: "", subject: "", message: "" });

  const custMap = useMemo(() => buildCustomerMap(customers, customerNames), [customers, customerNames]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  // Create/Cancel/Edit — the backend applies no role check on any of these
  // (any user who can reach this Sales Order can raise/tweak/cancel its
  // Proforma Invoice), so they're just "is a logged-in user on this page"
  // rather than a role allow-list. Delete is the one action that's actually
  // admin/planner-only on the backend — see roleAllowed for the alias match.
  const canCreate = !!user;
  const canCancel = !!user;
  const canEdit = !!user;
  const canDelete = roleAllowed(user?.role, ["admin", "planner"]);

  // Sales Orders eligible to raise a fresh Proforma Invoice against: not
  // cancelled, and no existing non-cancelled PI already raised for it.
  const eligibleCsos = useMemo(() => {
    // A PI raised from a DC doesn't count — the Sales Order may still get its own.
    const active = new Set(list.filter((p) => p.status !== "cancelled" && !p.source_dc_id).map((p) => p.source_cso_id));
    return csos.filter((c) => c.status !== "cancelled" && !active.has(c.id));
  }, [csos, list]);

  const load = async () => {
    const [p, s, c, n, i] = await Promise.all([
      api.get("/proforma-invoices"),
      api.get("/customer-sales-orders"),
      api.get("/customers"),
      api.get("/customers/names-lookup"),
      api.get("/items"),
    ]);
    setList(p.data);
    setCsos(s.data);
    setCustomers(c.data);
    setCustomerNames(n.data);
    setItems(i.data);
    if (selected) setSelected(p.data.find((x) => x.id === selected.id) || null);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Arriving here from Sales Orders' "Raise Proforma Invoice" button —
  // open the create dialog pre-selected to that Sales Order.
  useEffect(() => {
    const presetCsoId = location.state?.presetCsoId;
    if (presetCsoId) {
      setCreateCsoId(presetCsoId);
      setCreateOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Deep link — e.g. "View Proforma" / "Create Proforma Invoice" on a DC.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((pi) => {
      if (statusFilter !== "all" && pi.status !== statusFilter) return false;
      if (!q) return true;
      const cust = custMap[pi.customer_id];
      return (
        (pi.code || "").toLowerCase().includes(q) ||
        (pi.source_cso_code || "").toLowerCase().includes(q) ||
        (pi.source_dc_code || "").toLowerCase().includes(q) ||
        (pi.po_reference || "").toLowerCase().includes(q) ||
        (cust?.name || pi.customer_name || "").toLowerCase().includes(q) ||
        (cust?.code || "").toLowerCase().includes(q)
      );
    });
  }, [list, search, statusFilter, custMap]);

  const submitCreate = async () => {
    if (!createCsoId) return toast.error("Pick a Sales Order to raise the Proforma Invoice from");
    setBusy(true);
    try {
      const { data } = await api.post("/proforma-invoices", { cso_id: createCsoId });
      toast.success(`${data.code} raised`);
      setCreateOpen(false);
      setCreateCsoId("");
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const toggleAvailable = async (idx, checked) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/proforma-invoices/${selected.id}`, {
        line_availability: { [idx]: checked },
      });
      setSelected(data);
      setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A cancellation reason is required");
    setBusy(true);
    try {
      await api.post(`/proforma-invoices/${selected.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`${selected.code} cancelled`);
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const openLineEdit = (idx, line) => {
    setLineEditIdx(idx);
    setLineEditForm({
      qty: String(line.qty),
      price: String(line.price),
      discount_pct: String(line.discount_pct || 0),
      gst_pct: String((line.cgst_pct || 0) + (line.sgst_pct || 0) + (line.igst_pct || 0)),
      delivery_date: line.delivery_date ? new Date(line.delivery_date).toISOString().slice(0, 10) : "",
    });
  };

  const saveLineEdit = async () => {
    const qty = parseFloat(lineEditForm.qty);
    const price = parseFloat(lineEditForm.price);
    const discount_pct = parseFloat(lineEditForm.discount_pct || 0);
    const gst_pct = parseFloat(lineEditForm.gst_pct || 0);
    if (!qty || qty <= 0) return toast.error("Quantity must be greater than 0");
    if (isNaN(price) || price < 0) return toast.error("Rate cannot be negative");
    if (isNaN(discount_pct) || discount_pct < 0 || discount_pct > 100) return toast.error("Discount must be between 0 and 100");
    if (isNaN(gst_pct) || gst_pct < 0) return toast.error("GST cannot be negative");

    // Split the entered total GST% into CGST/SGST or IGST per the PI's GST
    // type — mirrors how Sales Orders split GST on their line form.
    const isInter = selected.gst_type === "inter";
    const cgst_pct = isInter ? 0 : gst_pct / 2;
    const sgst_pct = isInter ? 0 : gst_pct / 2;
    const igst_pct = isInter ? gst_pct : 0;
    const delivery_date = lineEditForm.delivery_date ? new Date(lineEditForm.delivery_date).toISOString() : null;

    // Backend takes the full line array (item/qty travel separately) — carry
    // every other line through unchanged and only apply the edited fields
    // to this row.
    const lines = selected.lines.map((l, i) => (
      i === lineEditIdx
        ? { item_id: l.item_id, price, discount_pct, cgst_pct, sgst_pct, igst_pct, delivery_date }
        : {
          item_id: l.item_id, price: l.price, discount_pct: l.discount_pct || 0,
          cgst_pct: l.cgst_pct || 0, sgst_pct: l.sgst_pct || 0, igst_pct: l.igst_pct || 0,
          delivery_date: l.delivery_date || null,
        }
    ));

    setBusy(true);
    try {
      const { data } = await api.patch(`/proforma-invoices/${selected.id}`, {
        lines, line_qty: { [lineEditIdx]: qty },
      });
      toast.success(`${selected.code} updated`);
      setLineEditIdx(null);
      setSelected(data);
      setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const submitDeleteLine = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/proforma-invoices/${selected.id}/lines/${lineDeleteIdx}`);
      toast.success(`Line removed from ${selected.code}`);
      setLineDeleteIdx(null);
      setSelected(data);
      setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const submitDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/proforma-invoices/${selected.id}`);
      toast.success(`${selected.code} deleted`);
      setDeleteOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const openEmail = () => {
    const cust = custMap[selected?.customer_id] || {};
    setEmailPayload({
      to_email: selected?.customer_email || cust.email || "",
      cc_email: "",
      bcc_email: "",
      subject: `Proforma Invoice ${selected.code}`,
      message: "Please find the attached Proforma Invoice. Reply to this email for any clarifications.",
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailPayload.to_email) return toast.error("Recipient email is required");
    setBusy(true);
    try {
      await api.post(`/proforma-invoices/${selected.id}/email`, {
        to_email: emailPayload.to_email,
        cc_email: emailPayload.cc_email || null,
        bcc_email: emailPayload.bcc_email || null,
        subject: emailPayload.subject || null,
        message: emailPayload.message || "",
      });
      toast.success(`Sent to ${emailPayload.to_email}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/proforma-invoices/${selected.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `ProformaInvoice-${selected.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.code}.pdf`);
    } catch { toast.error("PDF download failed"); }
  };

  const printPi = async () => {
    const r = await api.get(`/proforma-invoices/${selected.id}/render-html`, { responseType: "text" });
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${selected.code}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
    w.document.close();
  };

  const viewSalesOrder = (pi) => navigate("/customer-sales-orders", { state: { selectId: pi.source_cso_id } });

  // What the selected PI's Sales Order still has uncovered across all its
  // active PIs (ticked-available qty only) — short / unavailable lines can
  // go out on a Balance PI. Re-read whenever the selected PI changes.
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (!selected?.source_cso_id) { setBalance(null); return; }
    let live = true;
    api.get(`/customer-sales-orders/${selected.source_cso_id}/proforma-balance`)
      .then((r) => { if (live) setBalance(r.data); }).catch(() => { if (live) setBalance(null); });
    return () => { live = false; };
  }, [selected]);
  const raiseBalancePi = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-sales-orders/${selected.source_cso_id}/balance-proforma-invoice`);
      toast.success(`Balance Proforma Invoice ${data.code} raised`);
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not raise Balance PI");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Proforma Invoices
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Raised from a Sales Order — carries over every line. Tick an item once it's confirmed available; anything left
            unticked shows back on the Sales Order as pending.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="pi-new-btn">
            <PlusCircle className="w-4 h-4 mr-1.5" /> New Proforma Invoice
          </Button>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Proforma Invoice</DialogTitle>
            <DialogDescription>Pick a Sales Order — its customer, items, quantities, GST and grand total carry over as-is.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Sales Order *</Label>
            <Select value={createCsoId} onValueChange={setCreateCsoId}>
              <SelectTrigger data-testid="pi-create-cso-select"><SelectValue placeholder="Select a Sales Order..." /></SelectTrigger>
              <SelectContent>
                {eligibleCsos.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No Sales Order available — every open one already has a Proforma Invoice.</div>
                )}
                {eligibleCsos.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.code} — {custMap[c.customer_id]?.name || c.customer_name} · {formatMoney(Number(c.grand_total || 0), docCurrency(c))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={busy || !createCsoId} className="bg-blue-600 hover:bg-blue-700" data-testid="pi-create-submit">
              <Receipt className="w-4 h-4 mr-1.5" />Raise Proforma Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-line dialog — the per-row "Edit" action, full line detail */}
      <Dialog open={lineEditIdx !== null} onOpenChange={(v) => !v && setLineEditIdx(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Line Item</DialogTitle>
            <DialogDescription>
              {lineEditIdx !== null && selected && (
                <>{itemMap[selected.lines[lineEditIdx]?.item_id]?.name || selected.lines[lineEditIdx]?.part_name}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Quantity *</Label>
              <Input
                type="number" autoFocus value={lineEditForm.qty}
                onChange={(e) => setLineEditForm({ ...lineEditForm, qty: e.target.value })}
                data-testid="pi-line-edit-qty-input"
              />
            </div>
            <div>
              <Label>Rate ({currencySymbol(docCurrency(selected))}) *</Label>
              <Input
                type="number" value={lineEditForm.price}
                onChange={(e) => setLineEditForm({ ...lineEditForm, price: e.target.value })}
                data-testid="pi-line-edit-price-input"
              />
            </div>
            <div>
              <Label>Discount %</Label>
              <Input
                type="number" value={lineEditForm.discount_pct}
                onChange={(e) => setLineEditForm({ ...lineEditForm, discount_pct: e.target.value })}
                data-testid="pi-line-edit-discount-input"
              />
            </div>
            <div>
              <Label>GST %</Label>
              <Input
                type="number" value={lineEditForm.gst_pct}
                onChange={(e) => setLineEditForm({ ...lineEditForm, gst_pct: e.target.value })}
                data-testid="pi-line-edit-gst-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Delivery Date</Label>
              <Input
                type="date" value={lineEditForm.delivery_date}
                onChange={(e) => setLineEditForm({ ...lineEditForm, delivery_date: e.target.value })}
                data-testid="pi-line-edit-delivery-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLineEditIdx(null)}>Cancel</Button>
            <Button onClick={saveLineEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="pi-line-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-line dialog — the per-row "Delete" action */}
      <Dialog open={lineDeleteIdx !== null} onOpenChange={(v) => !v && setLineDeleteIdx(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this line?</DialogTitle>
            <DialogDescription>
              {lineDeleteIdx !== null && selected && (
                <>Removes {itemMap[selected.lines[lineDeleteIdx]?.item_id]?.name || selected.lines[lineDeleteIdx]?.part_name} from {selected.code}.
                  It goes back to pending on Sales Order <span className="mono">{selected.source_cso_code}</span>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLineDeleteIdx(null)}>Back</Button>
            <Button variant="destructive" onClick={submitDeleteLine} disabled={busy} data-testid="pi-line-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" />Remove Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {selected?.code}?</DialogTitle>
            <DialogDescription>
              This permanently removes the Proforma Invoice. Any items it left pending on Sales Order{" "}
              <span className="mono">{selected?.source_cso_code}</span> will be cleared. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={busy} data-testid="pi-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Why is this Proforma Invoice being cancelled?" data-testid="pi-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={busy} data-testid="pi-cancel-confirm">
              <XCircle className="w-4 h-4 mr-1.5" />Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Email Proforma Invoice {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To *</Label>
              <Input type="email" value={emailPayload.to_email} onChange={(e) => setEmailPayload({ ...emailPayload, to_email: e.target.value })} data-testid="pi-email-to" />
            </div>
            <div>
              <Label>Cc</Label>
              <Input type="email" multiple placeholder="comma-separated addresses" value={emailPayload.cc_email} onChange={(e) => setEmailPayload({ ...emailPayload, cc_email: e.target.value })} />
            </div>
            <div>
              <Label>Bcc</Label>
              <Input type="email" multiple placeholder="comma-separated addresses" value={emailPayload.bcc_email} onChange={(e) => setEmailPayload({ ...emailPayload, bcc_email: e.target.value })} />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailPayload.subject} onChange={(e) => setEmailPayload({ ...emailPayload, subject: e.target.value })} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={3} value={emailPayload.message} onChange={(e) => setEmailPayload({ ...emailPayload, message: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="pi-email-send"><Mail className="w-4 h-4 mr-1.5" />Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master / detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Proforma Invoices</span><span className="text-xs text-slate-500 mono">{visibleList.length}/{list.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 space-y-2">
            <Input
              placeholder="Search by PI/SO code, customer, PO ref..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="pi-search-input"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs" data-testid="pi-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {visibleList.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-pis">No proforma invoices match.</div>}
            {visibleList.map((pi) => {
              const availableCount = (pi.lines || []).filter((l) => l.available).length;
              const totalCount = (pi.lines || []).length;
              return (
                <button key={pi.id} onClick={() => setSelected(pi)} data-testid={`pi-row-${pi.code}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === pi.id ? "bg-blue-50/60" : ""} ${pi.status === "cancelled" ? "opacity-60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{pi.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[pi.status] || "bg-slate-100"}`}>{pi.status}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{custMap[pi.customer_id]?.name || pi.customer_name}</div>
                  <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                    SO <span className="mono">{pi.source_cso_code}</span>
                    {pi.source_dc_code && <> · DC <span className="mono">{pi.source_dc_code}</span></>}
                    {pi.is_balance && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Balance</span>}
                    {" · "}{formatMoney(Number(pi.grand_total || 0), docCurrency(pi))}
                    {pi.status !== "cancelled" && availableCount < totalCount && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                        {availableCount}/{totalCount} available
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="pi-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Receipt className="w-10 h-10 text-slate-300" /> Select a proforma invoice to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {custMap[selected.customer_id]?.name || selected.customer_name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-1">
                    Sales Order:{" "}
                    <button type="button" onClick={() => viewSalesOrder(selected)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="pi-so-link">
                      {selected.source_cso_code}<ExternalLink className="w-3 h-3" />
                    </button>
                    {selected.source_dc_code && (
                      <>
                        {" · "}DC No:{" "}
                        <button type="button" onClick={() => navigate("/delivery-challans", { state: { selectId: selected.source_dc_id } })}
                          className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="pi-dc-link">
                          {selected.source_dc_code}<ExternalLink className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    {" · "}Delivery Date: <span className="mono">{fmtDate(selected.delivery_date)}</span>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status] || "bg-slate-100"}`}>{selected.status}</span>
              </div>

              {selected.status === "cancelled" && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md" data-testid="pi-cancelled-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Cancelled{selected.cancelled_by ? ` by ${selected.cancelled_by}` : ""}{selected.cancelled_at ? ` on ${new Date(selected.cancelled_at).toLocaleString()}` : ""}</div>
                    {selected.cancel_reason && <div>Reason: {selected.cancel_reason}</div>}
                  </div>
                </div>
              )}

              {balance && balance.balance_qty > 0 && selected.status !== "cancelled" && (
                <div className="flex items-center gap-3 flex-wrap bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-md" data-testid="pi-balance-banner">
                  <div className="flex-1 min-w-[12rem]">
                    <div className="font-semibold">Balance on Sales Order {selected.source_cso_code}: {balance.balance_qty} qty not yet covered</div>
                    <div className="mt-0.5">
                      {balance.items.filter((i) => i.balance_qty > 0).map((i) => (
                        <span key={i.item_id} className="mr-3 mono">{itemMap[i.item_id]?.code || i.item_id}: {i.balance_qty} of {i.ordered_qty}</span>
                      ))}
                    </div>
                    {!balance.can_raise_balance && <div className="mt-0.5 text-amber-700">Tick the available lines first — the Balance PI carries what they leave short.</div>}
                  </div>
                  {balance.can_raise_balance && (
                    <Button size="sm" onClick={raiseBalancePi} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="pi-raise-balance-btn">
                      <PlusCircle className="w-4 h-4 mr-1.5" />Raise Balance PI
                    </Button>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap" data-testid="pi-actions">
                <Button variant="outline" size="sm" onClick={printPi} data-testid="pi-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
                <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="pi-pdf-btn"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
                <Button variant="outline" size="sm" onClick={openEmail} data-testid="pi-email-btn"><Mail className="w-4 h-4 mr-1.5" />Send Email</Button>
                {canCancel && selected.status !== "cancelled" && (
                  <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="ml-auto text-red-600 hover:text-red-700" data-testid="pi-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5" />Cancel
                  </Button>
                )}
                {canDelete && (
                  <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className={`text-red-600 hover:text-red-700 ${canCancel && selected.status !== "cancelled" ? "" : "ml-auto"}`} data-testid="pi-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" />Delete
                  </Button>
                )}
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic" data-testid="pi-notes">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Line Items</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Part No</th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">Qty</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3">Disc</th>
                        <th className="py-2 px-3">GST</th>
                        <th className="py-2 px-3">Delivery</th>
                        <th className="py-2 px-3">Amount</th>
                        <th className="py-2 px-3 text-center">Actions</th>
                        <th className="py-2 px-3 text-center">Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((l, idx) => {
                        const gross = l.qty * l.price;
                        const afterDisc = gross - gross * (l.discount_pct || 0) / 100;
                        const gstPct = (l.cgst_pct || 0) + (l.sgst_pct || 0) + (l.igst_pct || 0);
                        const lineTotal = afterDisc + afterDisc * gstPct / 100;
                        const rowLocked = busy || selected.status === "cancelled";
                        return (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`pi-line-${idx}`}>
                            <td className="py-2 px-3 mono text-xs text-slate-500">{itemMap[l.item_id]?.code || l.part_code || "—"}</td>
                            <td className="py-2 px-3 text-slate-700">{itemMap[l.item_id]?.name || l.part_name}</td>
                            <td className="py-2 px-3 mono">{l.qty} {l.uom}</td>
                            <td className="py-2 px-3 mono">{formatMoney(Number(l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3 mono">{l.discount_pct || 0}%</td>
                            <td className="py-2 px-3 mono">{gstPct}%</td>
                            <td className="py-2 px-3 mono text-xs">{fmtDate(l.delivery_date || selected.delivery_date)}</td>
                            <td className="py-2 px-3 mono font-semibold">{formatMoney(lineTotal, docCurrency(selected), { maximumFractionDigits: 2 })}</td>
                            <td className="py-2 px-3">
                              {canEdit && (
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost" size="icon" className="h-7 w-7"
                                    onClick={() => openLineEdit(idx, l)}
                                    disabled={rowLocked}
                                    title="Edit line item"
                                    data-testid={`pi-line-edit-${idx}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-slate-500" />
                                  </Button>
                                  <Button
                                    variant="ghost" size="icon" className="h-7 w-7"
                                    onClick={() => setLineDeleteIdx(idx)}
                                    disabled={rowLocked || selected.lines.length <= 1}
                                    title={selected.lines.length <= 1 ? "Delete the whole Proforma Invoice instead" : "Remove this line"}
                                    data-testid={`pi-line-delete-${idx}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                  </Button>
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <div className="inline-flex items-center gap-1.5">
                                <Checkbox
                                  checked={!!l.available}
                                  disabled={busy || selected.status === "cancelled"}
                                  onCheckedChange={(v) => toggleAvailable(idx, !!v)}
                                  data-testid={`pi-available-checkbox-${idx}`}
                                />
                                {l.available && (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid={`pi-available-tick-${idx}`} />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 w-80 text-xs">
                  <div className="flex justify-between py-0.5"><span>Subtotal</span><span className="mono">{formatMoney(Number(selected.subtotal || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>Discount</span><span className="mono">- {formatMoney(Number(selected.discount_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-0.5"><span>Tax</span><span className="mono">{formatMoney(Number(selected.tax_total || 0), docCurrency(selected), { decimals: 2 })}</span></div>
                  <div className="flex justify-between py-1 mt-1 border-t border-blue-200 font-bold"><span>Grand Total</span><span className="mono" data-testid="pi-detail-grand">{formatMoney(Number(selected.grand_total || 0), docCurrency(selected), { withCode: true })}</span></div>
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Ticking a line marks it available — it shows a check mark on the PDF. Leaving it unticked flags that item
                pending back on Sales Order <span className="mono">{selected.source_cso_code}</span>.
              </div>

              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2" data-testid="pi-detail-audit">
                Created by{" "}
                <span className="mono">
                  {selected.created_by_name ? `${selected.created_by_name} (${selected.created_by})` : (selected.created_by || "—")}
                </span>
                {" "}on {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
